import React, { useEffect, useState } from 'react';
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../../config/design/styles";
import { ChevronDown, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Tabs,
    Tab,
    useMediaQuery
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SubTitle from "../../../../components/elements/SubTitle";
import { theme } from "../../../../../config/design/theme";
import Grid from "@mui/material/Grid2";
import moment from 'moment-timezone';
import {setStore} from "trim-redux";
import {formatTimeRange, getDisplayTime} from "../../../../../config/values";

function VendorOrderTime({ timing, classes,setTime,edit }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(timing);
    // If the shop is open, default selected time is "now"; otherwise, it will be a proper timestamp when selected.
    const [selectedTime, setSelectedTime] = useState(timing.is_open ? "now" : "");
    // Default tab: 0 for Today if available, otherwise 1 for Tomorrow.
    const defaultTab = (timing.available_today && timing.available_today.length > 0) ? 0 : 1;
    const [tabValue, setTabValue] = useState(defaultTab);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Update state if the timing prop changes
    useEffect(() => {
        setData(timing);
        if (timing.is_open) {
            setSelectedTime("now");
        } else {
            setSelectedTime("");
        }
    }, [timing]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Ensure we have arrays for available times
    const todayAvailable = data.available_today || [];
    const tomorrowAvailable = data.available_tomorrow || [];

    const handleTimeSelection = (timeString) => {
        if(timeString === "now"){
            setSelectedTime("now");
            setTime("now");
            setStore({
                selected_time:"now"
            });
        }else{
            // Start with the current moment in Istanbul
            let currentMoment = moment.tz("Europe/Istanbul");
            // Adjust to tomorrow if needed
            if (tabValue === 1) {
                currentMoment = currentMoment.add(1, 'day');
            }
            // Split the time string "HH:mm" into hours and minutes.
            const [hours, minutes] = timeString.split(':').map(Number);
            // Create a moment object for the selected day and time in Istanbul timezone.
            const selectedMoment = moment.tz({
                year: currentMoment.year(),
                month: currentMoment.month(),
                day: currentMoment.date(),
                hour: hours,
                minute: minutes,
                second: 0,
                millisecond: 0
            }, "Europe/Istanbul");
            // Save the complete timestamp as a string that includes the Istanbul offset.
            setSelectedTime(selectedMoment.format('YYYY-MM-DDTHH:mm:ssZ'));
            setTime(selectedMoment.format('YYYY-MM-DDTHH:mm:ssZ'));
            setStore({
                selected_time:selectedMoment.format('YYYY-MM-DDTHH:mm:ssZ')
            });

        }

        setOpen(false);
    };

    /**
     * Helper to format the selectedTime for display.
     * - If nothing is selected, it shows a prompt.
     * - If "now" is selected, it displays "now".
     * - Otherwise, it displays the formatted date and time.
     */


    return (
        <div>
            {
                !!edit ?
                    <Button
                        startIcon={<Clock className="lucide-small" />}
                        onClick={() => setOpen(true)}
                        variant="outlined"
                        size="small"
                    >

                        {t('edit')}
                    </Button>
                    :
                    <Button
                        startIcon={<Clock className="lucide-small" />}
                        endIcon={<ChevronDown className="lucide-small" />}
                        onClick={() => setOpen(true)}
                        variant="outlined"
                        className="btn-white"
                    >

                        {(!isMobile || selectedTime === "now") && t('orderTime')+":"} {getDisplayTime(selectedTime)}
                    </Button>
            }


            <Dialog fullWidth maxWidth="sm" open={open} onClose={() => setOpen(false)}>
                <DialogTitle>
                    <SubTitle component="span" align="left" className="dialog-title">
                        {t('orderTime')}
                    </SubTitle>
                    <IconButton onClick={() => setOpen(false)} size="small" className={classes.dialogClose}>
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {/* Display the currently selected time */}
                        <Grid size={12}>
                            <Typography variant="h6" style={{ backgroundColor:"#eee", borderRadius:5, padding: 8, textAlign: "center",fontSize: 15}}>
                                {t('selectedTime')}: {getDisplayTime(selectedTime)}
                            </Typography>
                        </Grid>

                        {/* Tabs for Today and Tomorrow */}
                        <Grid size={12}>
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant="fullWidth"
                                indicatorColor="primary"
                                textColor="primary"
                            >
                                <Tab label={t('today')} disabled={todayAvailable.length === 0} />
                                <Tab label={t('tomorrow')} disabled={tomorrowAvailable.length === 0} />
                            </Tabs>
                        </Grid>

                        {/* Available time buttons */}
                        <Grid size={12}>
                            {tabValue === 0 && (
                                <>

                                    {todayAvailable.length > 0 ? (
                                        <div style={{ display: 'flex',flexDirection:'column', flexWrap: 'wrap', gap: '8px', marginTop: 8 }}>
                                            {timing.is_open && (
                                                <Button
                                                    color="secondary"
                                                    variant={selectedTime === "now" ? 'contained' : 'outlined'}
                                                    onClick={() => handleTimeSelection("now")}
                                                >
                                                    {t('now')}
                                                </Button>
                                            )}
                                            {todayAvailable.map((time, index) => (
                                                <Button
                                                    key={index}
                                                    color="secondary"
                                                    variant={
                                                        (selectedTime !== "now" &&
                                                            moment.tz(selectedTime, "Europe/Istanbul").hour() === Number(time.split(':')[0]) &&
                                                            moment.tz(selectedTime, "Europe/Istanbul").minute() === Number(time.split(':')[1]))
                                                            ? 'contained'
                                                            : 'outlined'
                                                    }
                                                    onClick={() => handleTimeSelection(time)}
                                                >
                                                    {formatTimeRange(time)}
                                                </Button>
                                            ))}
                                        </div>
                                    ) : (
                                        <Typography variant="body2">{t('noAvailableTimes')}</Typography>
                                    )}
                                </>
                            )}
                            {tabValue === 1 && (
                                <>
                                    {tomorrowAvailable.length > 0 ? (
                                        <div style={{ display: 'flex',flexDirection:'column', flexWrap: 'wrap', gap: '8px', marginTop: 8 }}>
                                            {tomorrowAvailable.map((time, index) => (
                                                <Button
                                                    key={index}
                                                    color="secondary"
                                                    variant={
                                                        (selectedTime !== "now" &&
                                                            moment.tz(selectedTime, "Europe/Istanbul").hour() === Number(time.split(':')[0]) &&
                                                            moment.tz(selectedTime, "Europe/Istanbul").minute() === Number(time.split(':')[1]))
                                                            ? 'contained'
                                                            : 'outlined'
                                                    }
                                                    onClick={() => handleTimeSelection(time)}
                                                >
                                                    {formatTimeRange(time)}
                                                </Button>
                                            ))}
                                        </div>
                                    ) : (
                                        <Typography variant="body2">{t('noAvailableTimes')}</Typography>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default withStyles(Styles)(VendorOrderTime);
